import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from 'src/components/system/Button';
import { Card, ErrorText, Input, Page, Title } from 'src/pages/Auth/styles';
import { listCourseCategories, CourseCategory } from 'src/services/categories';
import {
  AdaptiveInsightsPayload,
  AdaptiveReviewPlanPayload,
  FamilyPerformanceMetric,
  getAdaptiveInsights,
  getAdaptiveReviewPlan,
  getFamilyPerformance,
  getProfile,
  updateProfileName,
} from 'src/services/profile';
import { createAdaptiveSimulation, listSimulations } from 'src/services/simulations';
import { CFP_MODULES } from 'src/shared/data/cfp-modules';

function normalizeKey(value?: string): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const HIDDEN_FAMILY_SLUGS = new Set(['concurso', 'art-performance']);

const CARD_SLOTS = 12;

type SimulationHistoryItem = {
  id: number;
  status: string;
  correct: number;
  total: number;
  started_at: string;
  finished_at: string | null;
};

type ImprovementGap = {
  key: string;
  topicName: string;
  accuracy: number;
  avgTimeSeconds: number;
  weaknessScore: number;
  reviewRatio: number;
  newRatio: number;
  blockSize: number;
  reason: string;
};

type WeekRange = {
  start: Date;
  end: Date;
};

function getCurrentWeekRange(reference = new Date()): WeekRange {
  const start = new Date(reference);
  const dayOfWeek = start.getDay();
  const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  start.setDate(start.getDate() + offset);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

function formatDayMonth(value: Date): string {
  return value.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}

const Subtitle = styled.p`
  margin: 0;
  color: #5b5b5b;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #434343;
`;

const PerformanceSection = styled.div`
  margin: 48px auto 0;
  max-width: 1200px;
  width: 100%;
`;

const SectionHeader = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #0c1340;
  font-size: 1.75rem;
`;

const SectionDescription = styled.p`
  margin: 8px 0 0;
  color: #4a4a4a;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
`;

const MetricCard = styled.div`
  padding: 20px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 25px rgba(33, 37, 59, 0.08);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 220px;
`;

const MetricHeader = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #222b82;
`;

const MetricSlug = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8c92b3;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #4a4a4a;
`;

const MetricValue = styled.span`
  font-weight: 600;
  color: #222b82;
`;

const MetricAccuracy = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
`;

const AccuracyValue = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  color: #222b82;
`;

const AccuracyLabel = styled.span`
  font-size: 0.85rem;
  color: #384bab;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const ChartTrack = styled.div`
  height: 6px;
  border-radius: 999px;
  background: #e6e9ff;
  margin: 4px 0 8px;
  overflow: hidden;
`;

const ChartBar = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #222b82, #5f63ff);
`;

const MetricSummary = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #4a4a4a;
  margin-top: 4px;
`;

const MetricSummaryLabel = styled.span`
  color: #8c92b3;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.65rem;
`;

const MetricSummaryValue = styled.span`
  font-weight: 600;
`;

const MetricNote = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #6b6b6b;
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
`;

const ModuleCard = styled(MetricCard)`
  min-height: auto;
`;

const ModuleCardHeader = styled(MetricHeader)`
  margin: 0;
`;

const ModuleMeta = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #6d6d83;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
`;

const AdaptiveCard = styled.div`
  margin-top: 20px;
  padding: 24px;
  border-radius: 20px;
  background:
    radial-gradient(circle at 92% 15%, rgba(255, 255, 255, 0.22), transparent 34%),
    linear-gradient(120deg, #0f4d4f 0%, #1e4f79 56%, #365fbe 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 20px 42px rgba(15, 42, 66, 0.26);
`;

const AdaptiveList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ImprovementLayout = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ImprovementColumn = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.09);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ImprovementTitle = styled.strong`
  font-size: 0.95rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ImprovementHint = styled.span`
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.9rem;
`;

const GapList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
`;

const GapCard = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.09);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.85rem;
`;

const GapRank = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.76rem;
  letter-spacing: 0.05em;
  background: rgba(255, 255, 255, 0.2);
`;

const GoalProgressTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
`;

const GoalProgressBar = styled.div<{ width: number }>`
  width: ${({ width }) => `${Math.max(0, Math.min(100, width))}%`};
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #9df9e8 0%, #ffffff 100%);
  transition: width 420ms ease;
`;

const GoalMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-size: 0.82rem;
`;

const NextBlockGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 10px;
`;

const NextBlockCard = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.86rem;
`;

const DiagnosticsHeader = styled.div`
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DiagnosticsTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  color: #10354f;
`;

const DiagnosticsDescription = styled.p`
  margin: 0;
  color: #4a4a4a;
  font-size: 0.9rem;
`;

export function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [metrics, setMetrics] = useState<FamilyPerformanceMetric[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState('');
  const [adaptiveInsights, setAdaptiveInsights] = useState<AdaptiveInsightsPayload | null>(null);
  const [adaptiveLoading, setAdaptiveLoading] = useState(true);
  const [adaptiveReviewPlan, setAdaptiveReviewPlan] = useState<AdaptiveReviewPlanPayload | null>(null);
  const [adaptiveReviewPlanLoading, setAdaptiveReviewPlanLoading] = useState(true);
  const [simulationHistory, setSimulationHistory] = useState<SimulationHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'cadastro' | 'performance'>('cadastro');
  const [adaptiveGenerating, setAdaptiveGenerating] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'performance' || tab === 'cadastro') {
      setActiveTab(tab);
    } else {
      setActiveTab('cadastro');
    }
  }, [location.search]);

  useEffect(() => {
    document.title = 'Meu painel | ART - Simulatoro';
  }, []);

  useEffect(() => {
    (async () => {
      const profile = await getProfile();
      setName(profile.name);
      setEmail(profile.email);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    let active = true;
    setMetricsLoading(true);
    setMetricsError('');

    getFamilyPerformance()
      .then(data => {
        if (!active) return;
        setMetrics(
          data.filter(metric => {
            const slug = normalizeKey(metric.familySlug || metric.familyName);
            return !HIDDEN_FAMILY_SLUGS.has(slug);
          }),
        );
      })
      .catch(() => {
        if (!active) return;
        setMetricsError('Não foi possível carregar os indicadores de desempenho.');
      })
      .finally(() => {
        if (!active) return;
        setMetricsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setAdaptiveLoading(true);

    getAdaptiveInsights()
      .then(data => {
        if (!active) return;
        setAdaptiveInsights(data);
      })
      .catch(() => {
        if (!active) return;
        setAdaptiveInsights(null);
      })
      .finally(() => {
        if (!active) return;
        setAdaptiveLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setAdaptiveReviewPlanLoading(true);

    getAdaptiveReviewPlan({ topicsLimit: 5 })
      .then(data => {
        if (!active) return;
        setAdaptiveReviewPlan(data);
      })
      .catch(() => {
        if (!active) return;
        setAdaptiveReviewPlan(null);
      })
      .finally(() => {
        if (!active) return;
        setAdaptiveReviewPlanLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setCategoriesLoading(true);
    listCourseCategories()
      .then(data => {
        if (!active) return;
        const filtered = data.filter(category => !HIDDEN_FAMILY_SLUGS.has(normalizeKey(category.slug || category.name)));
        setCategories(filtered.slice(0, CARD_SLOTS));
      })
      .catch(() => {
        if (!active) return;
        setCategories([]);
      })
      .finally(() => {
        if (!active) return;
        setCategoriesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setHistoryLoading(true);

    listSimulations()
      .then(items => {
        if (!active) return;
        setSimulationHistory(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!active) return;
        setSimulationHistory([]);
      })
      .finally(() => {
        if (!active) return;
        setHistoryLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const metricsBySlug = useMemo(() => {
    const map: Record<string, FamilyPerformanceMetric> = {};
    for (const metric of metrics) {
      const slug = metric.familySlug?.trim().toLowerCase() ?? '';
      if (!slug || HIDDEN_FAMILY_SLUGS.has(normalizeKey(slug))) continue;
      map[slug] = metric;
    }
    return map;
  }, [metrics]);

  const topImprovementGaps = useMemo<ImprovementGap[]>(() => {
    const baseSizeRaw =
      adaptiveReviewPlan?.recommendation?.suggestedSimulation.size ??
      adaptiveInsights?.recommendation?.recommendedSize ??
      20;
    const baseSize = Number.isFinite(baseSizeRaw) ? Number(baseSizeRaw) : 20;

    if (adaptiveReviewPlan?.topics?.length) {
      return [...adaptiveReviewPlan.topics]
        .filter(topic => !HIDDEN_FAMILY_SLUGS.has(normalizeKey(topic.topicSlug || topic.topicName)))
        .sort((left, right) => right.weaknessScore - left.weaknessScore)
        .slice(0, 3)
        .map(topic => {
          const normalizedWeakness = Math.max(0.85, Math.min(1.35, topic.weaknessScore / 1.2));
          return {
            key: topic.topicKey,
            topicName: topic.topicName,
            accuracy: topic.accuracy,
            avgTimeSeconds: topic.avgTimeSeconds,
            weaknessScore: topic.weaknessScore,
            reviewRatio: topic.suggestedMix.reviewRatio,
            newRatio: topic.suggestedMix.newRatio,
            blockSize: Math.max(12, Math.round(baseSize * normalizedWeakness)),
            reason: `Fraqueza ${topic.weaknessScore.toFixed(2)} e acurácia ${topic.accuracy.toFixed(2)}%.`,
          };
        });
    }

    if (adaptiveInsights?.weaknesses?.length) {
      return [...adaptiveInsights.weaknesses]
        .filter(item => !HIDDEN_FAMILY_SLUGS.has(normalizeKey(item.familySlug || item.familyName)))
        .sort((left, right) => left.accuracy - right.accuracy || right.wrongAnswers - left.wrongAnswers)
        .slice(0, 3)
        .map(item => {
          const weaknessScore =
            item.totalQuestions > 0 ? Number((item.wrongAnswers / item.totalQuestions).toFixed(2)) : 0;
          const blockBase = item.recommendedSize > 0 ? item.recommendedSize : baseSize;

          return {
            key: item.familySlug || item.familyName,
            topicName: item.familyName || item.familySlug,
            accuracy: item.accuracy,
            avgTimeSeconds: item.avgTimePerQuestion,
            weaknessScore,
            reviewRatio: 70,
            newRatio: 30,
            blockSize: Math.max(12, Math.round(blockBase * 1.1)),
            reason: `Maior concentração de erros (${item.wrongAnswers} erros).`,
          };
        });
    }

    return [];
  }, [adaptiveInsights, adaptiveReviewPlan]);

  const weeklyGoal = useMemo(() => {
    const baseBlockRaw =
      adaptiveReviewPlan?.recommendation?.suggestedSimulation.size ??
      adaptiveInsights?.recommendation?.recommendedSize ??
      20;
    const baseBlock = Number.isFinite(baseBlockRaw) ? Number(baseBlockRaw) : 20;
    const targetQuestions = Math.max(60, Math.min(220, Math.round(baseBlock * 5)));
    const week = getCurrentWeekRange();

    let answeredQuestions = 0;
    let completedSimulations = 0;

    for (const item of simulationHistory) {
      if (item.status !== 'finished' || !item.finished_at) {
        continue;
      }

      const finishedAt = new Date(item.finished_at);
      if (Number.isNaN(finishedAt.getTime())) {
        continue;
      }

      if (finishedAt < week.start || finishedAt > week.end) {
        continue;
      }

      answeredQuestions += Math.max(0, Number(item.total ?? 0));
      completedSimulations += 1;
    }

    const progressPercent = targetQuestions > 0 ? Math.min(100, (answeredQuestions / targetQuestions) * 100) : 0;
    const remainingQuestions = Math.max(0, targetQuestions - answeredQuestions);

    return {
      targetQuestions,
      answeredQuestions,
      completedSimulations,
      progressPercent,
      remainingQuestions,
      weekRangeLabel: `${formatDayMonth(week.start)} - ${formatDayMonth(week.end)}`,
    };
  }, [adaptiveInsights, adaptiveReviewPlan, simulationHistory]);

  const showCfpModuleCards = true;

  const slotDefinitions = useMemo(() => {
    if (categories.length > 0) {
      const list = categories.map(category => ({
        slug: category.slug,
        name: category.name,
      }));
      while (list.length < CARD_SLOTS) {
        list.push({
          slug: `pendente-${list.length}`,
          name: 'Família',
        });
      }
      return list.slice(0, CARD_SLOTS);
    }

    const seen = new Set<string>();
    const fallback: { slug: string; name: string }[] = [];
    for (const metric of metrics) {
      const slug = metric.familySlug?.trim() ?? '';
      if (!slug) continue;
      const key = slug.toLowerCase();
      if (HIDDEN_FAMILY_SLUGS.has(normalizeKey(key))) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      fallback.push({
        slug,
        name: metric.familyName?.trim() || slug,
      });
      if (fallback.length >= CARD_SLOTS) {
        break;
      }
    }

    while (fallback.length < CARD_SLOTS) {
      fallback.push({
        slug: `pendente-${fallback.length}`,
        name: 'Família',
      });
    }

    return fallback;
  }, [categories, metrics]);

  const questionPoolBySlug = useMemo(() => {
    const map: Record<string, number> = {};
    for (const category of categories) {
      const slug = normalizeKey(category.slug || category.name || '');
      if (!slug || HIDDEN_FAMILY_SLUGS.has(slug)) {
        continue;
      }

      const questionCount =
        typeof category.questionCount === 'number' && Number.isFinite(category.questionCount)
          ? Number(category.questionCount)
          : 0;

      if (questionCount > 0) {
        map[slug] = questionCount;
      }
    }

    for (const metric of metrics) {
      const slug = normalizeKey(metric.familySlug || metric.familyName || '');
      const questionPool = typeof metric.questionPool === 'number' && Number.isFinite(metric.questionPool)
        ? Number(metric.questionPool)
        : 0;

      if (!slug || HIDDEN_FAMILY_SLUGS.has(slug) || questionPool <= 0) {
        continue;
      }

      map[slug] = questionPool;
    }

    return map;
  }, [categories, metrics]);

  function getFamilyQuestionPool(slugOrName?: string): number | null {
    const slug = normalizeKey(slugOrName);
    if (!slug) {
      return null;
    }
    const count = questionPoolBySlug[slug];
    return typeof count === 'number' && count > 0 ? count : null;
  }

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      toast.warn('Informe um nome válido.');
      return;
    }

    setSaving(true);
    try {
      const response = await updateProfileName(name.trim());
      setName(response.profile.name);
      setEmail(response.profile.email);
      if (response.persisted) {
        toast.success('Dados atualizados com sucesso!');
      } else {
        toast.info('Nome atualizado localmente. A API ainda não está disponível para sincronizar.');
      }
    } catch (error) {
      toast.error('Não foi possível atualizar seu nome agora.');
    } finally {
      setSaving(false);
    }
  }

  async function handleGenerateAdaptiveNow() {
    if (adaptiveGenerating) {
      return;
    }

    setAdaptiveGenerating(true);
    try {
      const response = await createAdaptiveSimulation({});
      navigate(`/simulate/${response.id}`);
    } catch (error) {
      toast.error('Não foi possível gerar o simulado inteligente agora.');
    } finally {
      setAdaptiveGenerating(false);
    }
  }

  if (loading) {
    return (
      <Page>
        <Card>
          <Title>Meu painel</Title>
          <Subtitle>Carregando informações...</Subtitle>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      {activeTab === 'cadastro' && (
        <ProfileGrid>
          <Card>
            <Title>Meu painel</Title>
            <Subtitle>Atualize seus dados e mantenha sua conta segura.</Subtitle>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Label>
                Nome completo
                <Input value={name} onChange={event => setName(event.target.value)} placeholder="Seu nome" required />
              </Label>
              <Label>
                Email
                <Input value={email} disabled readOnly />
              </Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Button
                  width="100%"
                  backgroundColor="#222B82"
                  borderColor="#222B82"
                  buttonColor="#fff"
                  type="submit"
                  disabled={saving}
                  label={<span>{saving ? 'Salvando...' : 'Salvar alterações'}</span>}
                />
                <Button
                  width="100%"
                  backgroundColor="#ffffff"
                  borderColor="#222B82"
                  buttonColor="#222B82"
                  type="button"
                  onClick={() => navigate('/auth/change-password')}
                  label={<span>Alterar senha</span>}
                />
              </div>
            </form>
          </Card>
        </ProfileGrid>
      )}

      {activeTab === 'performance' && (
        <PerformanceSection>
          <SectionHeader>
            <SectionTitle>Plano de Melhoria</SectionTitle>
            <SectionDescription>
              Priorize as lacunas com maior impacto, mantenha uma meta semanal e execute blocos focados por tópico.
            </SectionDescription>
          </SectionHeader>
          {(adaptiveLoading || adaptiveReviewPlanLoading) && (
            <Subtitle>Montando seu Plano de Melhoria...</Subtitle>
          )}
          {!adaptiveLoading &&
            !adaptiveReviewPlanLoading &&
            (topImprovementGaps.length > 0 || adaptiveInsights?.recommendation || adaptiveReviewPlan?.recommendation) && (
              <AdaptiveCard className="stagger-enter">
                <strong style={{ fontSize: '1.1rem' }}>Plano de Melhoria Inteligente</strong>
                <span>
                  {(adaptiveReviewPlan?.recommendation?.reason || adaptiveInsights?.recommendation?.reason) ??
                    'Estratégia montada com base nas suas últimas respostas e no ritmo de evolução.'}
                </span>
                <ImprovementLayout>
                  <ImprovementColumn>
                    <ImprovementTitle>Top 3 lacunas</ImprovementTitle>
                    {topImprovementGaps.length === 0 ? (
                      <ImprovementHint>Sem lacunas suficientes no momento. Continue praticando para gerar ranking.</ImprovementHint>
                    ) : (
                      <GapList>
                        {topImprovementGaps.map((gap, index) => (
                          <GapCard key={gap.key}>
                            <GapRank>#{index + 1}</GapRank>
                            <strong>{gap.topicName}</strong>
                            <span>Acurácia atual: {gap.accuracy.toFixed(2)}%</span>
                            <span>
                              Tempo médio: {gap.avgTimeSeconds.toFixed(2)}s | Score de lacuna: {gap.weaknessScore.toFixed(2)}
                            </span>
                          </GapCard>
                        ))}
                      </GapList>
                    )}
                  </ImprovementColumn>
                  <ImprovementColumn>
                    <ImprovementTitle>Meta semanal</ImprovementTitle>
                    {historyLoading ? (
                      <ImprovementHint>Calculando progresso com seus simulados da semana...</ImprovementHint>
                    ) : (
                      <>
                        <GoalMeta>
                          <span>Semana ({weeklyGoal.weekRangeLabel})</span>
                          <strong>
                            {weeklyGoal.answeredQuestions}/{weeklyGoal.targetQuestions} questões
                          </strong>
                        </GoalMeta>
                        <GoalProgressTrack>
                          <GoalProgressBar width={weeklyGoal.progressPercent} />
                        </GoalProgressTrack>
                        <GoalMeta>
                          <span>{weeklyGoal.completedSimulations} simulados concluídos</span>
                          <span>
                            {weeklyGoal.remainingQuestions > 0
                              ? `${weeklyGoal.remainingQuestions} para concluir a meta`
                              : 'Meta da semana concluída'}
                          </span>
                        </GoalMeta>
                      </>
                    )}
                    <Button
                      width="100%"
                      backgroundColor="#ffffff"
                      borderColor="#ffffff"
                      buttonColor="#0F4D4F"
                      type="button"
                      disabled={adaptiveGenerating}
                      onClick={handleGenerateAdaptiveNow}
                      label={<span>{adaptiveGenerating ? 'Gerando...' : 'Gerar simulado focado agora'}</span>}
                    />
                  </ImprovementColumn>
                </ImprovementLayout>
                {topImprovementGaps.length > 0 && (
                  <>
                    <ImprovementTitle>Próximo bloco por tópico</ImprovementTitle>
                    <NextBlockGrid>
                      {topImprovementGaps.map(gap => (
                        <NextBlockCard key={`next-${gap.key}`}>
                          <strong>{gap.topicName}</strong>
                          <span>
                            Bloco sugerido: {gap.blockSize} questões ({gap.reviewRatio}% revisão / {gap.newRatio}% inéditas)
                          </span>
                          <span>{gap.reason}</span>
                        </NextBlockCard>
                      ))}
                    </NextBlockGrid>
                  </>
                )}
                {adaptiveReviewPlan?.recommendation?.nextActions?.length ? (
                  <AdaptiveList>
                    {adaptiveReviewPlan.recommendation.nextActions.map(step => (
                      <li key={step}>{step}</li>
                    ))}
                  </AdaptiveList>
                ) : null}
              </AdaptiveCard>
            )}
          {metricsLoading && <Subtitle>Carregando desempenho...</Subtitle>}
          {metricsError && <ErrorText>{metricsError}</ErrorText>}
          {!metricsLoading && !metricsError && metrics.length === 0 && (
            <Subtitle>Você ainda não respondeu questões suficientes para gerar indicadores.</Subtitle>
          )}
          {!metricsLoading && (
            <>
              <DiagnosticsHeader>
                <DiagnosticsTitle>Diagnóstico detalhado por certificação</DiagnosticsTitle>
                <DiagnosticsDescription>
                  Use esse painel para validar progresso histórico e comparar acertos versus volume total por trilha.
                </DiagnosticsDescription>
              </DiagnosticsHeader>
              <MetricsGrid>
                {slotDefinitions.map(slot => {
                  const metric = metricsBySlug[slot.slug.toLowerCase()] ?? null;
                  const totalPool = getFamilyQuestionPool(slot.slug);
                  if (!metric) {
                    return (
                      <MetricCard key={slot.slug}>
                        <MetricHeader>{slot.name}</MetricHeader>
                        <MetricSlug>PENDENTE</MetricSlug>
                        <MetricRow>
                          <span>Questões respondidas</span>
                          <MetricValue>0</MetricValue>
                        </MetricRow>
                        <MetricRow>
                          <span>Acertos</span>
                          <MetricValue>0</MetricValue>
                        </MetricRow>
                        <MetricRow>
                          <span>Erros</span>
                          <MetricValue>0</MetricValue>
                        </MetricRow>
                        <MetricSummary>
                          <MetricSummaryLabel>Total respondidas</MetricSummaryLabel>
                          <MetricSummaryValue>{totalPool ? `0 de ${totalPool}` : '0'}</MetricSummaryValue>
                        </MetricSummary>
                        <ChartTrack>
                          <ChartBar style={{ width: '0%' }} />
                        </ChartTrack>
                        <MetricAccuracy>
                          <AccuracyValue>0.00%</AccuracyValue>
                          <AccuracyLabel>Precisão</AccuracyLabel>
                        </MetricAccuracy>
                        <MetricNote>Nenhuma atividade registrada ainda.</MetricNote>
                      </MetricCard>
                    );
                  }
                  const percent = metric.accuracy.toFixed(2);
                  const activityLabel = metric.lastActivityAt
                    ? new Date(metric.lastActivityAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'Sem atividade recente';

                  const slugSource = metric.familySlug?.trim() ?? '';
                  const formattedSlug = slugSource !== '' ? slugSource.toUpperCase() : '';
                  const fallbackName =
                    metric.familyName?.trim() ||
                    (formattedSlug !== ''
                      ? formattedSlug
                          .toLowerCase()
                          .split('-')
                          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                          .join(' ')
                      : '');
                  const displayName = fallbackName || 'Certificação';
                  const slugLabel = formattedSlug || 'FAMÍLIA';
                  const accuracyBar = Math.min(Math.max(metric.accuracy, 0), 100);

                  const responded = metric.totalQuestions ?? 0;
                  const familyPool = getFamilyQuestionPool(metric.familySlug ?? metric.familyName ?? '');

                  return (
                    <MetricCard key={metric.familySlug || metric.familyName || `${responded}-${percent}`}>
                      <MetricHeader>{displayName}</MetricHeader>
                      <MetricSlug>{slugLabel}</MetricSlug>
                      <MetricRow>
                        <span>Questões respondidas</span>
                        <MetricValue>{responded}</MetricValue>
                      </MetricRow>
                      <MetricRow>
                        <span>Acertos</span>
                        <MetricValue>{metric.correctAnswers}</MetricValue>
                      </MetricRow>
                      <MetricRow>
                        <span>Erros</span>
                        <MetricValue>{metric.incorrectAnswers}</MetricValue>
                      </MetricRow>
                      <MetricSummary>
                        <MetricSummaryLabel>Total respondidas</MetricSummaryLabel>
                        <MetricSummaryValue>{familyPool ? `${responded} de ${familyPool}` : `${responded}`}</MetricSummaryValue>
                      </MetricSummary>
                      <ChartTrack>
                        <ChartBar style={{ width: `${accuracyBar}%` }} />
                      </ChartTrack>
                      <MetricAccuracy>
                        <AccuracyValue>{percent}%</AccuracyValue>
                        <AccuracyLabel>Precisão</AccuracyLabel>
                      </MetricAccuracy>
                      <MetricNote>
                        Última atividade {activityLabel}
                        <br />
                        {metric.correctAnswers} de {responded || '0'} questões foram corretas
                      </MetricNote>
                    </MetricCard>
                  );
                })}
              </MetricsGrid>
              {showCfpModuleCards && (
                <ModuleGrid>
                  {CFP_MODULES.map(module => {
                    const metric = metricsBySlug[module.slug.toLowerCase()];
                    const answered = metric?.totalQuestions ?? 0;
                    const correct = metric?.correctAnswers ?? 0;
                    const errors = metric?.incorrectAnswers ?? 0;
                    const accuracy = metric ? Math.min(Math.max(metric.accuracy, 0), 100) : null;
                    const lastActivity = metric?.lastActivityAt
                      ? new Date(metric.lastActivityAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : 'Nenhuma';
                    const formattedSlug = module.slug.toUpperCase();
                    const modulePool = getFamilyQuestionPool(module.slug);
                    return (
                      <ModuleCard key={module.slug}>
                        <MetricHeader>{module.label}</MetricHeader>
                        <ModuleMeta>{module.description}</ModuleMeta>
                        <MetricSlug>{formattedSlug}</MetricSlug>
                        <MetricRow>
                          <span>Questões respondidas</span>
                          <MetricValue>{answered}</MetricValue>
                        </MetricRow>
                        <MetricRow>
                          <span>Acertos</span>
                          <MetricValue>{correct}</MetricValue>
                        </MetricRow>
                        <MetricRow>
                          <span>Erros</span>
                          <MetricValue>{errors}</MetricValue>
                        </MetricRow>
                        <MetricSummary>
                          <MetricSummaryLabel>Total respondidas</MetricSummaryLabel>
                          <MetricSummaryValue>{modulePool ? `${answered} de ${modulePool}` : `${answered}`}</MetricSummaryValue>
                        </MetricSummary>
                        <ChartTrack>
                          <ChartBar style={{ width: `${accuracy ?? 0}%` }} />
                        </ChartTrack>
                        <MetricAccuracy>
                          <AccuracyValue>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '--%'}</AccuracyValue>
                          <AccuracyLabel>Precisão</AccuracyLabel>
                        </MetricAccuracy>
                        <MetricNote>
                          Última atividade {lastActivity}
                          <br />
                          {metric?.correctAnswers ?? 0} de {metric?.totalQuestions ?? 0} questões foram corretas
                        </MetricNote>
                      </ModuleCard>
                    );
                  })}
                </ModuleGrid>
              )}
            </>
          )}
        </PerformanceSection>
      )}
    </Page>
  );
}
